import {
  Dialog,
  Field,
  FileUpload,
  type FileUploadRootProps,
  Portal,
} from "@ark-ui/react";
import {
  Eye,
  Paperclip,
  Plus,
  TrashSimple,
  UploadSimple,
  X,
} from "@phosphor-icons/react";
import clsx from "clsx";
import * as React from "react";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import { colors } from "../../tokens/colors.js";
import { Button } from "../button/Button.js";
import { Divider } from "../divider/Divider.js";
import { Text } from "../text/Text.js";

function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return "0 Bytes";

  const k: number = 1024;
  const dm: number = decimals < 0 ? 0 : decimals;
  const sizes: readonly string[] = [
    "Bytes",
    "KiB",
    "MiB",
    "GiB",
    "TiB",
    "PiB",
    "EiB",
    "ZiB",
    "YiB",
  ];

  const i: number = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

interface UploadProps extends FileUploadRootProps {
  label?: string;
  variant?: "button" | "extended";
  size?: "sm" | "md" | "lg";
  invalid?: boolean;
  asterisk?: boolean;
  helperText?: HelperTextProps;
  buttonLabel?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  classNames?: {
    root?: string;
    trigger?: string;
    label?: string;
    dropzone?: string;
    dropzoneControls?: string;
    helperText?: string;
  };
}

interface PictureUploadProps extends Omit<FileUploadRootProps, "maxFiles"> {
  variant?: "square" | "circle";
  invalid?: boolean;
  helperText?: HelperTextProps;
  classNames?: {
    root?: string;
    trigger?: string;
    label?: string;
    dropzone?: string;
    dropzoneControls?: string;
    helperText?: string;
  };
}

const Upload = React.forwardRef<
  React.ElementRef<typeof FileUpload.HiddenInput>,
  UploadProps
>((props, ref) => {
  const {
    label,
    variant = "button",
    size = "md",
    helperText = { message: "", status: "default" },
    asterisk = false,
    classNames,
    invalid,
    buttonLabel = "Upload",
    startIcon,
    endIcon,
    ...rest
  } = props;
  return (
    <Field.Root invalid={invalid}>
      <FileUpload.Root
        data-invalid={invalid}
        data-size={size}
        data-variant={variant}
        className={clsx("ox-reset ox-FileUploadRoot", classNames?.root)}
        {...rest}
      >
        {label ? (
          <FileUpload.Label data-size={size} className="ox-reset ox-FormLabel">
            {label}
            {asterisk ? (
              <span className={clsx("ox-reset ox-FormLabelAsterisk")}> *</span>
            ) : null}
          </FileUpload.Label>
        ) : null}
        {variant === "button" ? (
          <FileUpload.Trigger
            data-variant={variant}
            data-size={size}
            data-invalid={invalid}
            className={clsx(
              "ox-reset ox-FileUploadTrigger",
              classNames?.trigger,
            )}
          >
            {startIcon && (
              <span className="ox-UploadButton-iconSlot">{startIcon}</span>
            )}
            {buttonLabel}
            {endIcon && (
              <span className="ox-UploadButton-iconSlot">{endIcon}</span>
            )}
          </FileUpload.Trigger>
        ) : (
          <FileUpload.Dropzone
            data-variant={variant}
            data-invalid={invalid}
            className={clsx(
              "ox-reset ox-FileUploadDropzone",
              classNames?.dropzone,
            )}
          >
            <div className={clsx("ox-reset ox-FileUploadDropzoneIcon")}>
              <UploadSimple size={36} fill="currentColor" />
            </div>
            <div
              className={clsx(
                "ox-reset ox-FileUploadDropzoneControls",
                classNames?.dropzoneControls,
              )}
            >
              <Text fontWeight="medium" size="md">
                Drag and drop to upload file(s)
              </Text>
              <Divider text="or" />
              <FileUpload.Trigger asChild>
                <Button size="sm" className={clsx("ox-reset")}>
                  {buttonLabel}
                </Button>
              </FileUpload.Trigger>
            </div>
          </FileUpload.Dropzone>
        )}

        <FileUpload.Context>
          {({ acceptedFiles }) =>
            acceptedFiles.length > 0 ? (
              <FileUpload.ItemGroup
                className={clsx("ox-reset ox-FileUploadItemGroup")}
              >
                {acceptedFiles.map((file) => (
                  <FileUpload.Item
                    key={file.name}
                    file={file}
                    className={clsx("ox-reset ox-FileUploadItem")}
                  >
                    <FileUpload.ItemPreview type=".*">
                      <Paperclip color={colors.neutral.grey[600]} size={16} />
                    </FileUpload.ItemPreview>

                    <FileUpload.ItemName
                      title={`${file.name} - ${formatBytes(file.size)}`}
                    />

                    <FileUpload.ItemDeleteTrigger className="ox-reset">
                      <TrashSimple color={colors.neutral.grey[600]} size={16} />
                    </FileUpload.ItemDeleteTrigger>
                  </FileUpload.Item>
                ))}
              </FileUpload.ItemGroup>
            ) : null
          }
        </FileUpload.Context>

        <FileUpload.HiddenInput ref={ref} />

        {helperText?.message ? (
          <Field.HelperText
            data-size={"md"}
            data-status={helperText?.status}
            className={clsx(
              "ox-reset ox-FormHelperText",
              classNames?.helperText,
            )}
          >
            {getFormHelperText(helperText, "md")}
          </Field.HelperText>
        ) : null}
      </FileUpload.Root>
    </Field.Root>
  );
});

const PictureUpload = React.forwardRef<
  React.ElementRef<typeof FileUpload.HiddenInput>,
  PictureUploadProps
>((props, ref) => {
  const {
    variant = "square",
    invalid,
    accept = ["image/*"],
    helperText = { message: "", status: "default" },
    classNames,
    ...rest
  } = props;

  return (
    <Field.Root invalid={invalid}>
      <FileUpload.Root
        data-variant={variant}
        data-invalid={invalid}
        maxFiles={1}
        accept={accept}
        className={clsx("ox-reset ox-PictureUploadRoot")}
        {...rest}
      >
        <div className={clsx("ox-reset ox-PictureUploadWrapper")}>
          <FileUpload.Dropzone
            data-variant={variant}
            data-invalid={invalid}
            className={clsx("ox-reset ox-PictureUploadDropzone")}
          >
            <FileUpload.Trigger
              data-variant={variant}
              data-invalid={invalid}
              className={clsx("ox-reset ox-PictureUploadTrigger")}
            >
              <Plus size={16} fill="currentColor" />

              <Text size="sm">Upload</Text>
            </FileUpload.Trigger>
          </FileUpload.Dropzone>

          <FileUpload.Context>
            {({ acceptedFiles }) =>
              acceptedFiles.length > 0 ? (
                <FileUpload.ItemGroup
                  className={clsx("ox-reset ox-PictureUploadWrapper")}
                >
                  {acceptedFiles.map((file) => (
                    <FileUpload.Item
                      key={file.name}
                      file={file}
                      data-variant={variant}
                      data-invalid={invalid}
                      className={clsx("ox-reset ox-PictureUploadItem")}
                    >
                      <FileUpload.ItemPreview
                        data-variant={variant}
                        data-invalid={invalid}
                        className={clsx("ox-reset ox-PictureUploadPreview")}
                        type="image/*"
                      >
                        <div
                          data-variant={variant}
                          className={clsx(
                            "ox-reset ox-PictureUploadPreviewWrapper",
                          )}
                        >
                          <FileUpload.ItemPreviewImage />

                          <div
                            className={clsx(
                              "ox-reset ox-PictureUploadPreviewControls",
                            )}
                          >
                            <Dialog.Root>
                              <Dialog.Trigger
                                className={clsx(
                                  "ox-reset ox-PictureUploadPreviewControl",
                                )}
                              >
                                <Eye fill="currentColor" size={20} />
                              </Dialog.Trigger>
                              <Portal>
                                <Dialog.Backdrop
                                  className={clsx(
                                    "ox-reset ox-PictureUploadDialogOverlay",
                                  )}
                                />
                                <Dialog.Positioner>
                                  <Dialog.Content
                                    className={clsx(
                                      "ox-reset ox-PictureUploadDialogContent",
                                    )}
                                  >
                                    <FileUpload.ItemPreviewImage
                                      className={clsx(
                                        "ox-reset ox-PictureUploadDialogPreviewImage",
                                      )}
                                    />
                                    <FileUpload.ItemName
                                      title={`${file.name} - ${formatBytes(
                                        file.size,
                                      )}`}
                                    />
                                    <Dialog.CloseTrigger
                                      className={clsx(
                                        "ox-reset ox-PictureUploadDialogClose",
                                      )}
                                    >
                                      <X size={24} fill="currentColor" />
                                    </Dialog.CloseTrigger>
                                  </Dialog.Content>
                                </Dialog.Positioner>
                              </Portal>
                            </Dialog.Root>

                            <FileUpload.ItemDeleteTrigger
                              className={clsx(
                                "ox-reset ox-PictureUploadPreviewControl",
                              )}
                            >
                              <TrashSimple size={20} fill="currentColor" />
                            </FileUpload.ItemDeleteTrigger>
                          </div>
                        </div>
                      </FileUpload.ItemPreview>
                    </FileUpload.Item>
                  ))}
                </FileUpload.ItemGroup>
              ) : null
            }
          </FileUpload.Context>
        </div>

        <FileUpload.HiddenInput ref={ref} />

        {helperText?.message ? (
          <Field.HelperText
            data-size={"md"}
            data-status={helperText?.status}
            className={clsx(
              "ox-reset ox-FormHelperText",
              classNames?.helperText,
            )}
          >
            {getFormHelperText(helperText, "md")}
          </Field.HelperText>
        ) : null}
      </FileUpload.Root>
    </Field.Root>
  );
});

Upload.displayName = "Upload";
PictureUpload.displayName = "PictureUpload";

export { Upload, PictureUpload };
export type { UploadProps, PictureUploadProps };
